import { Suspense, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import * as D from '@radix-ui/react-dialog';
import { LogOut, Menu, PanelLeftClose, PanelLeftOpen, Power, Search } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { Button, Skeleton } from '@/components/ui';
import { cn } from '@/lib/utils';
import { BrandMark } from './brand-mark';
import { CommandPalette } from './command-palette';
import { NavList } from './nav';
import { OutboundStatus, useSystemControls } from './outbound-status';

const wide = () => typeof window !== 'undefined' && window.matchMedia?.('(min-width: 1024px)').matches;

export function AppShell() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [expanded, setExpanded] = useState(wide);
  const [drawer, setDrawer] = useState(false);
  const [palette, setPalette] = useState(false);
  const main = useRef<HTMLElement>(null);
  const first = useRef(true);
  const { data: controls } = useSystemControls();

  // Focus management on route change (a11y floor): move focus to <main>, skip first paint.
  useEffect(() => {
    if (first.current) first.current = false;
    else main.current?.focus({ preventScroll: true });
    setDrawer(false);
  }, [pathname]);

  return (
    <div className="min-h-screen">
      <a
        href="#main"
        className="sr-only z-[100] rounded-md bg-accent px-3 py-2 font-semibold text-fg-on-accent focus:not-sr-only focus:fixed focus:left-2 focus:top-2"
      >
        Skip to content
      </a>

      {/* Desktop / tablet rail */}
      <aside
        aria-label="Primary"
        className={cn(
          'fixed inset-y-0 left-0 z-30 hidden flex-col border-r border-line-subtle bg-surface md:flex',
          expanded ? 'w-52' : 'w-14',
        )}
      >
        <div className={cn('flex h-11 items-center border-b border-line-subtle px-3', !expanded && 'justify-center px-0')}>
          <BrandMark showText={expanded} />
        </div>
        <NavList expanded={expanded} />
        <div className="border-t border-line-subtle p-2">
          <Button
            variant="ghost"
            size="icon"
            aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
            aria-expanded={expanded}
            onClick={() => setExpanded((e) => !e)}
          >
            {expanded ? <PanelLeftClose className="size-4" aria-hidden /> : <PanelLeftOpen className="size-4" aria-hidden />}
          </Button>
        </div>
      </aside>

      {/* Phone drawer */}
      <D.Root open={drawer} onOpenChange={setDrawer}>
        <D.Portal>
          <D.Overlay className="fixed inset-0 z-40 bg-black/60 md:hidden" />
          <D.Content
            aria-describedby={undefined}
            className="fixed inset-y-0 left-0 z-50 flex w-60 flex-col border-r border-line bg-surface shadow-overlay md:hidden"
          >
            <D.Title className="sr-only">Navigation</D.Title>
            <div className="flex h-11 items-center border-b border-line-subtle px-3">
              <BrandMark />
            </div>
            <NavList expanded onNavigate={() => setDrawer(false)} />
          </D.Content>
        </D.Portal>
      </D.Root>

      <div className={cn('flex min-h-screen min-w-0 flex-col', expanded ? 'md:pl-52' : 'md:pl-14')}>
        <header className="sticky top-0 z-20 flex h-11 items-center gap-2 border-b border-line-subtle bg-surface px-4">
          <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open navigation" onClick={() => setDrawer(true)}>
            <Menu className="size-[18px]" aria-hidden />
          </Button>
          <div className="md:hidden">
            <BrandMark showText={false} />
          </div>
          <button
            type="button"
            onClick={() => setPalette(true)}
            className="flex h-7 min-w-0 max-w-sm flex-1 items-center gap-2 rounded-sm border border-line bg-inset px-2 text-sm text-fg-subtle hover:border-line-strong pointer-coarse:h-11"
          >
            <Search className="size-3.5 shrink-0" aria-hidden />
            <span className="truncate">Search or jump to</span>
            <kbd className="ml-auto hidden rounded-sm border border-line px-1 font-mono text-2xs sm:inline">Ctrl K</kbd>
          </button>
          <span className="flex-1" />
          <OutboundStatus />
          <span className="hidden text-sm text-fg-muted sm:inline" data-testid="whoami">
            {user?.name} <span className="font-mono text-xs">({user?.role})</span>
          </span>
          <Button variant="ghost" size="icon" aria-label="Log out" onClick={() => void logout()}>
            <LogOut className="size-4" aria-hidden />
          </Button>
        </header>

        {controls?.killSwitch && (
          <div role="alert" className="flex items-center gap-2 bg-danger px-4 py-1.5 text-sm font-semibold text-fg-on-accent">
            <Power className="size-4" aria-hidden />
            Sending halted (kill switch). Nothing sends while this is engaged.
          </div>
        )}

        <main
          id="main"
          ref={main}
          tabIndex={-1}
          className="mx-auto w-full min-w-0 max-w-[1480px] flex-1 p-4 focus:outline-none"
        >
          <Suspense fallback={<Skeleton className="h-8 w-56" />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      <CommandPalette open={palette} onOpenChange={setPalette} />
    </div>
  );
}
